import { useEffect, useMemo } from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { useCurrentUser } from "src/business-hooks/useCurrentUser";
import { useQueryUsersPublic } from "src/queries/users/users.query";
import { useSearchStorage } from "src/storage/useSearchStorage";
import { DROPDOWN_SURFACE } from "src/theme/dropdownSurface";
import { useShowFilters } from "./AssetsFilters.hooks";
import AssetsFilterTags from "./components/AssetsFilterTags/AssetsFilterTags";
import TimeWindow from "./components/TimeWindow/TimeWindow";

const AssetsFilters = ({ ...other }) => {
  const { showUsersFilter, showTagsFilter, showTimeFilter } = useShowFilters();

  const {
    timeWindow,
    setTimeWindow,
    appOwners = [],
    setAppOwners,
  } = useSearchStorage();

  const loggedInUser = useCurrentUser();

  const { data: users = [] } = useQueryUsersPublic();

  const additionalUsers = useMemo(
    () => [
      {
        username:
          "Me " + (loggedInUser?.username && "(" + loggedInUser?.username + ")"),
        user_id: loggedInUser?.user_id,
      },
      {
        username: "Any Owner",
        user_id: 0, //user_id = 0  represents all users
      },
      {
        username: "No Owners",
        user_id: -1, //user_id = -1 represents no owners
      },
    ],
    [loggedInUser?.username, loggedInUser?.user_id]
  );

  // Get all available user IDs
  const availableUserIds = useMemo(() => {
    const allUsers = [...additionalUsers, ...users];
    return allUsers.map((u) => u.user_id).filter((id) => id !== undefined);
  }, [additionalUsers, users]);

  // Automatically clean up invalid appOwners
  useEffect(() => {
    if (appOwners.length > 0) {
      const validOwners = appOwners.filter((ownerId) =>
        availableUserIds.includes(ownerId)
      );
      if (validOwners.length !== appOwners.length) {
        setAppOwners(validOwners);
      }
    }
  }, [appOwners, availableUserIds, setAppOwners]);

  return (
    <Box
      data-tour="dashboard-filters"
      data-test-id="app-filters"
      data-ph-capture-attribute-feature="global_filters"
      className="flex justify-between items-center pr-1"
      {...other}
      sx={other.sx}
    >
      <div className="w-full flex justify-evenly gap-3">
        {showTimeFilter && (
          <TimeWindow timeWindow={timeWindow!} setTimeWindow={setTimeWindow} />
        )}
        {showUsersFilter && users?.length > 0 && (
          <FormControl sx={{ m: 0, minWidth: 150, maxWidth: 170 }} size="small">
            <InputLabel>Pipeline Owner</InputLabel>
            <Select
              labelId="pipeline-owner-label"
              data-ph-capture-attribute-action="filter_pipeline_owner"
              multiple
              value={appOwners}
              onChange={(event) => {
                const value = event.target.value;
                if (typeof value !== "string") {
                  setAppOwners(value);
                } else {
                  setAppOwners([]);
                }
              }}
              input={<OutlinedInput label="Pipeline Owner" />}
              renderValue={(selected) =>
                [...additionalUsers, ...users]
                  .filter((u) => selected.indexOf(u.user_id as number) !== -1)
                  .map((u) => u.username)
                  .join(", ")
              }
              MenuProps={{
                slotProps: {
                  paper: {
                    sx: DROPDOWN_SURFACE,
                    style: {
                      maxHeight: 400,
                    },
                  },
                },
              }}
            >
              {additionalUsers.map((user) => (
                <MenuItem
                  key={user.user_id}
                  value={user.user_id}
                  sx={{ paddingY: 0, paddingX: 0.5 }}
                >
                  <Checkbox
                    checked={appOwners.indexOf(user?.user_id as number) > -1}
                    size="small"
                  />
                  <ListItemText primary={user.username} />
                </MenuItem>
              ))}
              <Divider />
              {users
                ?.filter((u) => u.user_id !== loggedInUser?.user_id)
                .map((user) => (
                  <MenuItem
                    key={user.user_id}
                    value={user.user_id}
                    data-ph-capture-attribute-owner={user.username}
                    sx={{ paddingY: 0, paddingX: 0.5 }}
                  >
                    <Checkbox
                      checked={appOwners?.indexOf(user.user_id) > -1}
                      size="small"
                    />
                    <ListItemText primary={user.username} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        {showTagsFilter && <AssetsFilterTags />}
      </div>
    </Box>
  );
};

export default AssetsFilters;
